import type { Property, District, PropertyCategory, ListingType } from '../types/cms';

export const districts: { value: District | 'all'; label: string }[] = [
  { value: 'all', label: '所有' },
  { value: 'tsuen_wan', label: '荃灣' },
  { value: 'kwai_chung', label: '葵涌' },
  { value: 'lai_chi_kok', label: '荔枝角' },
  { value: 'other', label: '其他' },
];

export const categories: { value: PropertyCategory | 'all'; label: string }[] = [
  { value: 'all', label: '所有' },
  { value: 'office', label: '寫字樓' },
  { value: 'industrial', label: '工商 / 貨倉' },
  { value: 'shop', label: '地舖' },
  { value: 'car_park', label: '車位' },
  { value: 'other', label: '其他' },
];

export const listingTypes: { value: ListingType | 'all'; label: string }[] = [
  { value: 'all', label: '所有' },
  { value: 'rent', label: '放租' },
  { value: 'sale', label: '放售' },
  { value: 'sale_or_rent', label: '租售均可' },
];

export const salePriceOptions = [
  { value: 'all', label: '所有價錢' },
  { value: 'sale-under-3m', label: '300萬以下' },
  { value: 'sale-3m-6m', label: '300-600萬' },
  { value: 'sale-6m-10m', label: '600-1000萬' },
  { value: 'sale-over-10m', label: '1000萬以上' },
];

export const rentPriceOptions = [
  { value: 'all', label: '所有價錢' },
  { value: 'rent-under-10k', label: '$10,000以下' },
  { value: 'rent-10k-20k', label: '$10,000-$20,000' },
  { value: 'rent-20k-30k', label: '$20,000-$30,000' },
  { value: 'rent-over-30k', label: '$30,000以上' },
];

export interface PropertyFilterState {
  listingType: ListingType | 'all';
  district: District | 'all';
  category: PropertyCategory | 'all';
  salePriceRange: string;
  rentPriceRange: string;
  keyword: string;
  agentId?: string;
  featured?: 'all' | 'yes' | 'no';
}

export const defaultPropertyFilters: PropertyFilterState = {
  listingType: 'all',
  district: 'all',
  category: 'all',
  salePriceRange: 'all',
  rentPriceRange: 'all',
  keyword: '',
  agentId: 'all',
  featured: 'all',
};

const checkSalePrice = (p: Property, range: string): boolean => {
  if (range === 'all' || !range) return true;
  // sale_or_rent also has sale price in `price`
  if (p.listingType === 'rent') return false;
  switch (range) {
    case 'sale-under-3m':
      return p.price < 3000000;
    case 'sale-3m-6m':
      return p.price >= 3000000 && p.price <= 6000000;
    case 'sale-6m-10m':
      return p.price > 6000000 && p.price <= 10000000;
    case 'sale-over-10m':
      return p.price > 10000000;
    default:
      return true;
  }
};

const checkRentPrice = (p: Property, range: string): boolean => {
  if (range === 'all' || !range) return true;
  // sale_or_rent may also have rentPrice
  const price = p.rentPrice ?? 0;
  if (p.listingType === 'sale') return false;
  if (price === 0) return false;
  switch (range) {
    case 'rent-under-10k':
      return price < 10000;
    case 'rent-10k-20k':
      return price >= 10000 && price <= 20000;
    case 'rent-20k-30k':
      return price > 20000 && price <= 30000;
    case 'rent-over-30k':
      return price > 30000;
    default:
      return true;
  }
};

export const filterProperties = (
  properties: Property[],
  filters: PropertyFilterState
) => {
  const keyword = filters.keyword?.trim().toLowerCase() || '';

  return properties.filter((p) => {
    const matchesListing = filters.listingType === 'all' || !filters.listingType || p.listingType === filters.listingType;
    const matchesDistrict = filters.district === 'all' || !filters.district || p.district === filters.district;
    const matchesCategory = filters.category === 'all' || !filters.category || p.category === filters.category;
    const matchesAgent = filters.agentId === 'all' || !filters.agentId || p.agentId === filters.agentId;
    const matchesFeatured =
      filters.featured === 'all' || !filters.featured
        ? true
        : filters.featured === 'yes'
        ? p.featured
        : !p.featured;

    const matchesSalePrice = checkSalePrice(p, filters.salePriceRange);
    const matchesRentPrice = checkRentPrice(p, filters.rentPriceRange);

    const searchText = [p.title, p.location, p.address, p.code, ...p.tags].join(' ').toLowerCase();
    const matchesKeyword = !keyword || searchText.includes(keyword);

    return matchesListing && matchesDistrict && matchesCategory && matchesSalePrice && matchesRentPrice && matchesKeyword && matchesAgent && matchesFeatured;
  });
};
